import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../api/apiClient";
import { AuthContext } from "../context/AuthContext";

import {
  ContactType,
  ServiceAttributeCategory,
  RelationshipType,
  OrganisationType,
  ServiceRegistrationComponentStatic,
  ServiceRegistrationMemberTypeStatic,
  ServiceCategory,
  ServiceCategoryGroup,
} from "../pages/types/types";

import { RelationshipCategory } from "../constants/constants";

export interface StaticData {
  contactTypes: ContactType[];
  serviceAttributeCategories: ServiceAttributeCategory[];
  relationshipTypes: Partial<Record<RelationshipCategory, RelationshipType[]>>;
  organisationTypes: OrganisationType[];
  serviceCategories?: ServiceCategory[];
  serviceCategoryGroups?: ServiceCategoryGroup[];
  serviceRegistrationMemberTypes?: ServiceRegistrationMemberTypeStatic[];
  serviceRegistrationComponents?: ServiceRegistrationComponentStatic[];
}

interface StaticDataContextType {
  data: StaticData | null;
  refresh: () => Promise<void>;
  setPublicData: (data: StaticData) => void;
}

const StaticDataContext = createContext<StaticDataContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "frontline_static_data_v8";

function safeParseStaticData(value: string | null): StaticData | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as StaticData;

    // Static data cached before the classification release does not contain
    // these catalogues. Discard it so a reload fetches the current API shape.
    if (
      !Array.isArray(parsed.serviceCategoryGroups) ||
      !Array.isArray(parsed.serviceCategories) ||
      parsed.serviceCategories.length === 0
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export const StaticDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("StaticDataProvider must be used within AuthProvider");
  }

  const { isAuthenticated } = authContext;

  const [data, setData] = useState<StaticData | null>(() =>
    safeParseStaticData(sessionStorage.getItem(STORAGE_KEY)),
  );

  const loadStaticData = async () => {
    // Prevent loading if not authenticated
    if (!isAuthenticated) return;

    // Prevent duplicate loading if already cached
    if (data?.serviceCategoryGroups?.length) return;

    const result = (await api("/api/staticdata/all", {
      method: "GET",
      suppressLoading: true,
    })) as StaticData;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    setData(result);
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!data?.serviceCategoryGroups?.length) {
      loadStaticData();
    }
  }, [isAuthenticated]); // eslint-disable-line

  const refresh = async () => {
    if (!isAuthenticated) return;

    sessionStorage.removeItem(STORAGE_KEY);

    const result = (await api("/api/staticdata/all", {
      method: "GET",
    })) as StaticData;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    setData(result);
  };

  const setPublicData = useCallback((publicData: StaticData) => {
    setData(publicData);
  }, []);

  return (
    <StaticDataContext.Provider value={{ data, refresh, setPublicData }}>
      {children}
    </StaticDataContext.Provider>
  );
};

export const useStaticData = () => {
  const context = useContext(StaticDataContext);
  if (!context)
    throw new Error("useStaticData must be used within StaticDataProvider");

  return context;
};
